import type { EquipmentPurpose } from '@/types/equipment'
import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type { Cycle, EventType } from '@/types/equipment'
import type { AuditTrailRow } from '@/types/audit'

// ─── Backend row shapes (tenant-scoped equipment registry) ──────────────────

export type ApiEquipmentGroup = {
  id: string
  name: string
  /** A Room Group: made on, and listed on, the Cleaning page only (z8nrz7ewt1). */
  forRooms?: boolean
}

/** A tenant event-type catalog row — code is server-slugified from the label. */
export type ApiEventType = {
  id: string
  code: string
  label: string
  /** Hex color, e.g. "#2563eb". */
  color: string
  order: number
  /** True for the seeded standard types — editable but not deletable (409). */
  isSystem: boolean
}

export type ApiMaintenanceSchedule = {
  id: string
  equipmentTypeId: string
  cycle: Cycle
  eventTypes: EventType[]
  weeklyDays: { day: number; state: 'required' | 'preferred' }[] | null
  preferredTiming: string | null
  /** Optional time of day (HH:mm 24h). */
  startTime: string | null
  /** Optional window end time (HH:mm 24h). */
  endTime: string | null
  note: string | null
  /** The procedure each event type on this cycle runs (z8nrz7f9u6), with its name for display. */
  procedures?: { eventTypeCode: EventType; procedureTemplate: { id: string; name: string; type: string } }[]
}

export type ApiCalendarEvent = {
  id: string
  equipmentTypeId: string
  /** ISO datetime from the backend; date part is the meaningful bit. */
  date: string
  event: string
  note: string | null
}

/** A single authored pre-start checklist item for an equipment type. */
export type ApiPreStartChecklistItem = {
  id: string
  label: string
  required: boolean
  order: number
}

export type ApiEquipmentType = {
  id: string
  name: string
  groupId: string
  group: ApiEquipmentGroup
  /**
   * What the type is FOR — a coarser axis than `group`, which names a
   * manufacturing stage. The two are independent: Cryopreservation holds both a
   * controlled-rate freezer (processing) and an LN2 dewar (storage).
   *
   * Optional on the wire so a response from a backend predating the column still
   * parses; readers should treat a missing value as `PROCESSING`, which is what
   * the column defaults to.
   */
  purpose?: EquipmentPurpose
  /**
   * Whether this kind of machine is serviced on a cycle at all.
   *
   * A separate axis from `purpose` and not derivable from it: a pharmacy fridge
   * is STORAGE *and* calibrated, while ambient shelving is STORAGE and never
   * touched. False means the backend refuses schedules and calendar events for
   * the type and skips it during generation.
   *
   * Optional on the wire for the same reason as `purpose` — a response from a
   * backend predating the column still parses, and a missing value reads as
   * `true`, which is the column default.
   */
  maintained?: boolean
  /** Room types (Cleaning Visual): hours a clean holds after its task is signed off; null clears. */
  cleanHoldTimeHours?: number | null
  schedules: ApiMaintenanceSchedule[]
  calendarEvents: ApiCalendarEvent[]
  unitCount: number
  /** Ordered pre-start checklist authored for this type. */
  preStartChecklist: ApiPreStartChecklistItem[]
  /**
   * Technician a generated PM task for this type is assigned to
   * (`POST /equipment/events/generate`). Absent/null means generated events
   * stay unassigned — surfaced in the worklist's Unassigned lane, not dropped.
   */
  defaultAssigneeId?: string | null
  defaultAssignee?: { id: string; fullName: string | null; username: string | null; email: string } | null
}

export type ApiEquipmentUnit = {
  id: string
  label: string
  equipmentTypeId: string
  equipmentType: ApiEquipmentType & { group: ApiEquipmentGroup }
  model: string | null
  serial: string | null
  vendor: string | null
  installedYear: number | null
  locationSite: string | null
  locationFloor: string | null
  locationBay: string | null
  description: string | null
  /** OUT_OF_SERVICE units are refused at booking. */
  status?: 'OPERATIONAL' | 'OUT_OF_SERVICE'
  statusNote?: string | null
  /** The floor a room is mapped to in the site layout (z8nrz7f731); null = not mapped. */
  floorId?: string | null
  /** The room it stands in (z8nrz7fw25): equipment of purpose ROOM. Null = in none yet. */
  roomId?: string | null
  /**
   * Where it is, from the site layout: a unit's room path, or the floor a room
   * is on ("NYP › Building 1 › 2nd Floor › Room 5"). Null until it is mapped.
   */
  place?: string | null
  /** Rooms: cleanroom grade and floor area (Cleaning Visual). */
  grade?: string | null
  areaSqm?: number | null
}

/**
 * What `GET /equipment/resolve` answers with — identity and type, not the whole
 * registry row. A scan asks "which machine is this", so the type name is
 * flattened onto the answer rather than arriving as a nested record.
 */
export type ApiResolvedEquipment = {
  id: string
  label: string
  status: 'OPERATIONAL' | 'OUT_OF_SERVICE'
  statusNote: string | null
  equipmentTypeId: string
  equipmentTypeName: string
}

/** One expanded, dated occurrence from the backend recurrence engine. */
export type ApiOccurrence = {
  id: string
  /** ISO date, e.g. "2026-10-01". */
  date: string
  equipmentTypeId: string
  /** Equipment type name. */
  equipmentType: string
  /** Group name. */
  group: string
  eventType: EventType
  /** Display label, e.g. "Preventive Maintenance (PM)" or "Vendor PM visit". */
  label: string
  /** Hex color resolved from the event-type catalog (may be absent for one-offs). */
  color?: string
  /** True for recurring schedule occurrences, false for one-off calendar events. */
  routine: boolean
  note?: string
  /** Optional time of day (HH:mm 24h), carried from the schedule cell. */
  startTime?: string
  /** Optional window end time (HH:mm 24h), carried from the schedule cell. */
  endTime?: string

  // ─── Repeating-task paints only ────────────────────────────────────────────
  //
  // A schedule occurrence is per TYPE and leaves these undefined — it means
  // "applies to all N units of this type", and nothing exists behind it yet. A
  // repeating task names ONE machine and IS a real MaintenanceEvent, so these
  // are what tell the UI that Start/Complete here acts on an actual task.
  /** The MaintenanceEvent this paint belongs to. */
  eventId?: string
  equipmentId?: string
  /** Unit label, e.g. "BSC-01". */
  equipmentLabel?: string
  /** The task's cadence, so the calendar can say "repeats weekly". */
  repeatCycle?: RepeatCycle
}

/** Cadences a single task may repeat at — mirrors the backend REPEAT_CYCLES. */
export const REPEAT_CYCLES = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
  'semiannually',
  'yearly',
] as const

export type RepeatCycle = (typeof REPEAT_CYCLES)[number]

/** Human labels for the cadence picker. */
export const REPEAT_CYCLE_LABELS: Record<RepeatCycle, string> = {
  daily: 'Daily (business days)',
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannually: 'Every 6 months',
  yearly: 'Yearly',
}

/** A persisted maintenance work-order event (GET /equipment/events). */
export type ApiMaintenanceEvent = {
  id: string
  /** Null for a task on a material instead of a unit (z8nrz7fjzc). */
  equipmentId: string | null
  equipmentTypeId: string | null
  /** The material (Material.id) an Inventory task is on, when it is not on a unit. */
  materialId?: string | null
  /** Optionally one lot of that material (z8nrz7fjzc). */
  lotId?: string | null
  /** Or a purchase order: receiving it, an Inventory task (z8nrz7fjzc). */
  purchaseOrderId?: string | null
  eventTypeCode: EventType
  /** "preventive" | "corrective" | "inventory" (made by an inventory action). */
  taskClass: string
  /** TRIGGER: made by an inventory action — a lot received, an order sent, a transfer ready (z8nrz7fjzc). */
  source: 'RECURRING' | 'CALENDAR' | 'AD_HOC' | 'TRIGGER'
  earlyStart: string | null
  plannedStart: string | null
  /** ISO date. */
  dueDate: string
  planHours: number | null
  priority: number
  usedBy: string | null
  /** When the task was created (ISO datetime). */
  createdAt?: string
  /** The procedure run opened for this task (Pending Actions), or null until then. */
  procedureId?: string | null
  sopRef: string | null
  assigneeId: string | null
  status: 'NOT_STARTED' | 'STARTED' | 'COMPLETED'
  /** Cadence this one task re-paints on the calendar at; null = a one-off. */
  repeatCycle: RepeatCycle | null
  actualStart: string | null
  actualEnd: string | null
  actualHours: number | null
  /**
   * Derived by the server in the tenant's timezone with its grace, never
   * stored (z8nrz7dyc7). Overdue ignores status apart from completion, so a
   * STARTED event can be overdue. `daysOverdue` counts from the due date and is
   * null unless overdue. `completedLate` is the separate "ran late" marker and
   * is never true of an overdue event.
   *
   * Only the list feed carries them. Create / start / end / update return the
   * bare row, so a missing flag reads as "not overdue" until the list refetches.
   */
  overdue?: boolean
  daysOverdue?: number | null
  completedLate?: boolean
  /** Joined display fields from the feed's include. */
  /** `status` is the unit's now, for the out-of-service badge (z8nrz7fkgd). */
  equipment: { label: string; status?: 'OPERATIONAL' | 'OUT_OF_SERVICE' } | null
  equipmentType: { name: string } | null
  material?: { materialCode: string; materialName: string; materialType: string } | null
  lot?: { lotNumber: string } | null
  purchaseOrder?: { number: string; supplierName: string } | null
  assignee: { fullName: string | null; username: string | null; email: string } | null
  /** This event's equipment type's pre-start checklist, ordered. */
  preStartChecklist: { id: string; label: string; required: boolean }[]
  /**
   * The procedure (forms) the task carries (z8nrz7fgzw): its run's template once
   * opened, else what its cycle links. Null when it carries none.
   */
  procedureTemplate?: { id: string; name: string } | null
  /**
   * For a REPEATING task, the days inside the requested window it is worked on.
   * Empty for an ordinary task, which is simply due on its due date.
   *
   * A repeat is due once, at the end of its series, so placing it by `dueDate`
   * alone would show it in one week out of fifty-two. Present only when the
   * request carried a from/to window.
   */
  occurrenceDates?: string[]
}

/** Filters for the worklist / calendar feed. */
export type ListEventsQuery = {
  from?: string
  to?: string
  status?: 'NOT_STARTED' | 'STARTED' | 'COMPLETED'
  taskClass?: 'preventive' | 'corrective' | 'inventory'
  assigneeId?: string
  /** Only events overdue now, as the server derives it. Combines with the rest. */
  overdue?: boolean
  /** Only events on equipment of these purposes (z8nrz7ewt2). Omitted: every purpose. */
  purposes?: readonly EquipmentPurpose[]
  /**
   * Tasks on materials of these types too (z8nrz7fjzc), matched without regard
   * to case. With `purposes`, either matches; alone, only material tasks.
   */
  materialTypes?: readonly string[]
  /** Purchase orders' tasks too (z8nrz7fjzc): Inventory asks for them. */
  orders?: boolean
  /** Only tasks inventory actions made (z8nrz7fjzc): Inventory lists those and no unit's maintenance. */
  triggered?: boolean
  /** At most this many, the latest by due date first (the unbounded "All" view). */
  limit?: number
}

/** What the stat cards count: the list's own range and subjects (z8nrz7fjzc). */
export type EventStatsQuery = Pick<
  ListEventsQuery,
  'from' | 'to' | 'purposes' | 'materialTypes' | 'orders' | 'triggered' | 'assigneeId'
>

/**
 * The worklist's headline counts (`GET /equipment/events/stats`), over the same
 * tasks the list shows (z8nrz7fjzc). Due today, In progress, Overdue, Completed
 * and Not started split them with no overlap, so they add up to `total`.
 * `dueTodayAll` is today's whatever range is asked for (the subtitle).
 */
export type ApiEventStats = {
  total: number
  dueToday: number
  inProgress: number
  overdue: number
  completed: number
  notStarted: number
  dueTodayAll: number
}

/**
 * One task waiting on the procedure its cycle carries (GET /equipment/pending-actions,
 * z8nrz7ewt1). `procedureId` is null until the run is opened; opening creates it.
 */
export type PendingActionRow = {
  eventId: string
  equipmentId: string
  equipmentLabel: string
  equipmentTypeName: string
  purpose: EquipmentPurpose
  eventTypeCode: string
  /** The cycle whose procedure this is; null when the link was removed after the run was opened. */
  cycle: string | null
  /** `YYYY-MM-DD`. */
  dueDate: string
  earlyStart: string | null
  status: 'NOT_STARTED' | 'STARTED'
  overdue: boolean
  daysOverdue: number | null
  assigneeId: string | null
  assigneeName: string | null
  procedureTemplateId: string
  procedureTemplateName: string
  procedureId: string | null
  completedForms: number
  totalForms: number
}

/** `?purposes=PROCESSING,STORAGE`, or nothing when there is no filter. */
function purposesParam(purposes?: readonly EquipmentPurpose[]): string {
  return purposes && purposes.length > 0 ? `purposes=${encodeURIComponent(purposes.join(','))}` : ''
}

/** `path` with one person's filter appended (z8nrz7fgzw), or unchanged for everyone. */
function withAssignee(path: string, assigneeId?: string | null): string {
  return assigneeId ? `${path}${path.includes('?') ? '&' : '?'}assigneeId=${encodeURIComponent(assigneeId)}` : path
}

/** `path` with the material-types filter appended (z8nrz7fjzc). */
/** The events feed's query string; the stat cards send the same filters. */
function eventsQuery(params: ListEventsQuery): string {
  const qs = new URLSearchParams()
  if (params.from) qs.set('from', params.from)
  if (params.to) qs.set('to', params.to)
  if (params.status) qs.set('status', params.status)
  if (params.taskClass) qs.set('taskClass', params.taskClass)
  if (params.assigneeId) qs.set('assigneeId', params.assigneeId)
  if (params.overdue) qs.set('overdue', 'true')
  if (params.purposes?.length) qs.set('purposes', params.purposes.join(','))
  if (params.materialTypes?.length) qs.set('materialTypes', params.materialTypes.join(','))
  if (params.orders) qs.set('orders', 'true')
  if (params.triggered) qs.set('triggered', 'true')
  if (params.limit) qs.set('limit', String(params.limit))
  const query = qs.toString()
  return query ? `?${query}` : ''
}

/** `path` with the purposes filter appended. */
function withPurposes(path: string, purposes?: readonly EquipmentPurpose[]): string {
  const p = purposesParam(purposes)
  return p ? `${path}${path.includes('?') ? '&' : '?'}${p}` : path
}

/**
 * Start transition payload (e-signature captured as metadata).
 *
 * No `signedBy`: the server resolves the signer from the authenticated
 * principal. `signingToken` is the single-use proof they re-entered their
 * password, minted by `lib/signingToken.ts`.
 */
export type StartEventPayload = {
  signingToken: string
  signatureMeaning: string
  comments?: string
  checklist?: { itemId?: string; label: string; checked: boolean }[]
  /** Why maintenance starts on a storage unit that still holds stock; the server requires it then. */
  evacuationOverrideReason?: string
}

/** End transition payload — actual-vs-plan + deviation + e-signature. */
export type EndEventPayload = {
  signingToken: string
  signatureMeaning: string
  actualHours?: number
  result?: string
  deviation?: string
}

/** Undo-End payload — reopen a completed event with a mandatory reason. */
export type UndoEndPayload = { signingToken: string; reason: string }

/** Out of service → Available by hand (z8nrz7fzyk): signed, with a reason. */
export type ReturnToServicePayload = { signingToken: string; reason: string }

/** A technician who can be assigned a maintenance event. */
/** `email` disambiguates staff who share a display name. */
export type AssignableUser = { id: string; name: string; email: string }

/** Edit patch: reschedule / reassign / edit fields. `assigneeId: ''` unassigns. */
export type UpdateEventPayload = {
  dueDate?: string
  assigneeId?: string
  priority?: number
  planHours?: number
  earlyStart?: string
  sopRef?: string
  usedBy?: string
  actor?: string
  /** New cadence, or '' to stop repeating without deleting the task. */
  repeatCycle?: RepeatCycle | ''
}

/** Ad-hoc create payload — an unplanned CM / out-of-cycle PM. */
export type CreateEventPayload = {
  /** The unit the task is on; or `materialId` instead (z8nrz7fjzc). Exactly one. */
  equipmentId?: string
  materialId?: string
  /** Optionally one lot of `materialId`: a task on the lot rather than the whole material. */
  lotId?: string
  eventTypeCode: string
  taskClass?: 'preventive' | 'corrective'
  dueDate: string
  earlyStart?: string
  priority?: number
  planHours?: number
  usedBy?: string
  sopRef?: string
  assigneeId?: string
  reason?: string
  /**
   * Cadence at which this ONE task re-paints on the calendar until completed.
   * Preventive only; no further tasks are created from it.
   */
  repeatCycle?: RepeatCycle
  /** The form this task runs (z8nrz7fjzc), over its cycle's. Omitted: the cycle's, if any. */
  procedureTemplateId?: string
}

export type CreateEquipmentPayload = {
  label: string
  equipmentTypeId: string
  model?: string
  serial?: string
  vendor?: string
  installedYear?: number
  locationSite?: string
  locationFloor?: string
  locationBay?: string
  description?: string
  /** Rooms only: null clears. */
  grade?: string | null
  areaSqm?: number | null
  /** The room it stands in (z8nrz7fw25); null takes it out. Never a room itself. */
  roomId?: string | null
}

export type UpsertSchedulePayload = {
  eventTypes: EventType[]
  weeklyDays?: { day: number; state: 'required' | 'preferred' }[]
  preferredTiming?: string
  startTime?: string
  endTime?: string
  note?: string
  /**
   * The procedure each event type runs (z8nrz7f9u6): the whole set, so an event
   * type left out runs none. Omitted leaves the links as they are.
   */
  procedures?: { eventTypeCode: EventType; procedureTemplateId: string }[]
}

/** Shared fetch wrapper — bearer token + `X-Tenant-Id` header (integrationsApi pattern). */
async function request<T>(path: string, tenantId: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    ...init,
    headers: authHeaders({
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId,
      ...init?.headers,
    }),
  })
  handleUnauthorized(res.status)
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string | string[] } | null
    const message = Array.isArray(body?.message) ? body.message.join('; ') : body?.message
    // See the matching note in `inventoryApi.ts`: the status rides along so the
    // scan lookup can tell 403 from 404. Additive — every existing caller reads
    // only `.message`.
    throw Object.assign(new Error(message || `Request failed: ${res.status}`), {
      status: res.status,
    })
  }
  return res.json() as Promise<T>
}

export const equipmentApi = {
  /**
   * One unit's audit trail (z8nrz7dq6q): maintenance events, bookings and bench
   * scans, newest first, in the same row shape as a form's trail.
   */
  getAuditTrail: (tenantId: string, equipmentId: string) =>
    request<AuditTrailRow[]>(`/equipment/${equipmentId}/audit`, tenantId),

  /** Groups holding a type of these purposes, plus empty ones (z8nrz7ewt2). Omitted: every group. */
  listGroups: (tenantId: string, purposes?: readonly EquipmentPurpose[]) =>
    request<ApiEquipmentGroup[]>(withPurposes('/equipment/groups', purposes), tenantId),

  createGroup: (tenantId: string, payload: { name: string; forRooms?: boolean }) =>
    request<ApiEquipmentGroup>('/equipment/groups', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateGroup: (tenantId: string, id: string, payload: { name: string }) =>
    request<ApiEquipmentGroup>(`/equipment/groups/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteGroup: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/equipment/groups/${id}`, tenantId, { method: 'DELETE' }),

  listEventTypes: (tenantId: string) =>
    request<ApiEventType[]>('/equipment/event-types', tenantId),

  createEventType: (tenantId: string, payload: { label: string; color: string; order?: number }) =>
    request<ApiEventType>('/equipment/event-types', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateEventType: (
    tenantId: string,
    id: string,
    payload: { label?: string; color?: string; order?: number },
  ) =>
    request<ApiEventType>(`/equipment/event-types/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteEventType: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/equipment/event-types/${id}`, tenantId, { method: 'DELETE' }),

  listTypes: (tenantId: string, purposes?: readonly EquipmentPurpose[]) =>
    request<ApiEquipmentType[]>(withPurposes('/equipment/types', purposes), tenantId),

  createType: (
    tenantId: string,
    payload: {
      name: string
      groupId: string
      /** Omitted means PROCESSING, matching the column default. */
      purpose?: EquipmentPurpose
      /** Omitted means maintained, matching the column default. */
      maintained?: boolean
      preStartChecklist?: { label: string; required?: boolean }[]
      /** Omitted means no default — generated events stay unassigned. */
      defaultAssigneeId?: string
      cleanHoldTimeHours?: number
    },
  ) =>
    request<ApiEquipmentType>('/equipment/types', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateType: (
    tenantId: string,
    id: string,
    payload: {
      name?: string
      groupId?: string
      purpose?: EquipmentPurpose
      /** Turning this off stops all scheduling for the type; see ApiEquipmentType. */
      maintained?: boolean
      preStartChecklist?: { label: string; required?: boolean }[]
      /** Omitted leaves it unchanged; `''` clears an existing default. */
      defaultAssigneeId?: string
      cleanHoldTimeHours?: number | null
    },
  ) =>
    request<ApiEquipmentType>(`/equipment/types/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  listOccurrences: (
    tenantId: string,
    params: { from: string; to: string; groupId?: string; purposes?: readonly EquipmentPurpose[] },
  ) => {
    const qs = new URLSearchParams({ from: params.from, to: params.to })
    if (params.groupId) qs.set('groupId', params.groupId)
    if (params.purposes?.length) qs.set('purposes', params.purposes.join(','))
    return request<ApiOccurrence[]>(`/equipment/occurrences?${qs.toString()}`, tenantId)
  },

  /**
   * Materialise recurrence occurrences into persisted per-unit MaintenanceEvents
   * for [from, to) — idempotent, safe to re-run over an overlapping range.
   */
  generateEvents: (
    tenantId: string,
    payload: { from: string; to: string; groupId?: string; purposes?: readonly EquipmentPurpose[] },
  ) =>
    request<{ generated: number; skipped: number; total: number }>(
      '/equipment/events/generate',
      tenantId,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  listEvents: (tenantId: string, params: ListEventsQuery = {}) =>
    request<ApiMaintenanceEvent[]>(`/equipment/events${eventsQuery(params)}`, tenantId),

  /**
   * The Tasks page's stat cards, counted by the server over the same tasks the
   * list shows — its range and subjects — without downloading them (z8nrz7f9u6,
   * z8nrz7fjzc).
   */
  eventStats: (tenantId: string, params: EventStatsQuery = {}) =>
    request<ApiEventStats>(`/equipment/events/stats${eventsQuery(params)}`, tenantId),

  /** Tasks waiting on their cycle's procedure, soonest due first (z8nrz7ewt1). */
  listPendingActions: (tenantId: string, purposes?: readonly EquipmentPurpose[], assigneeId?: string | null) =>
    request<PendingActionRow[]>(withAssignee(withPurposes('/equipment/pending-actions', purposes), assigneeId), tenantId),

  /**
   * Start a task that carries a procedure (z8nrz7fgzw): opens its run and marks
   * the task started, with no Start signature — the forms carry them.
   */
  startTaskProcedure: (tenantId: string, eventId: string) =>
    request<{ procedureId: string; started: boolean }>(`/equipment/events/${eventId}/start-procedure`, tenantId, {
      method: 'POST',
    }),

  /** The task's procedure run: created the first time, the same one after. */
  openTaskProcedure: (tenantId: string, eventId: string) =>
    request<{ procedureId: string; created: boolean }>(`/equipment/events/${eventId}/procedure`, tenantId, {
      method: 'POST',
    }),

  listAssignableUsers: (tenantId: string) =>
    request<AssignableUser[]>('/equipment/assignable-users', tenantId),

  updateEvent: (tenantId: string, id: string, payload: UpdateEventPayload) =>
    request<ApiMaintenanceEvent>(`/equipment/events/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  createEvent: (tenantId: string, payload: CreateEventPayload) =>
    request<ApiMaintenanceEvent>('/equipment/events', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  startEvent: (tenantId: string, id: string, payload: StartEventPayload) =>
    request<ApiMaintenanceEvent>(`/equipment/events/${id}/start`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  endEvent: (tenantId: string, id: string, payload: EndEventPayload) =>
    request<ApiMaintenanceEvent>(`/equipment/events/${id}/end`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  undoEndEvent: (tenantId: string, id: string, payload: UndoEndPayload) =>
    request<ApiMaintenanceEvent>(`/equipment/events/${id}/undo-end`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  returnToService: (tenantId: string, id: string, payload: ReturnToServicePayload) =>
    request<ApiEquipmentUnit>(`/equipment/${id}/return-to-service`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listEquipment: (tenantId: string, purposes?: readonly EquipmentPurpose[]) =>
    request<ApiEquipmentUnit[]>(withPurposes('/equipment', purposes), tenantId),

  /**
   * Which unit a scanned asset tag names.
   *
   * The server re-decodes the code rather than trusting the caller's parse, so
   * a malformed value rejects with 400 and an unknown one with 404 carrying
   * "No equipment matches this code." — the message the scan surface renders.
   */
  resolveScan: (tenantId: string, code: string) =>
    request<ApiResolvedEquipment>(
      `/equipment/resolve?code=${encodeURIComponent(code)}`,
      tenantId,
    ),

  createEquipment: (tenantId: string, payload: CreateEquipmentPayload) =>
    request<ApiEquipmentUnit>('/equipment', tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateEquipment: (tenantId: string, id: string, payload: Partial<CreateEquipmentPayload>) =>
    request<ApiEquipmentUnit>(`/equipment/${id}`, tenantId, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  upsertSchedule: (
    tenantId: string,
    equipmentTypeId: string,
    cycle: Cycle,
    payload: UpsertSchedulePayload,
  ) =>
    request<ApiMaintenanceSchedule | { ok: true }>(
      `/equipment/types/${equipmentTypeId}/schedules/${cycle}`,
      tenantId,
      { method: 'PUT', body: JSON.stringify(payload) },
    ),

  createCalendarEvent: (
    tenantId: string,
    equipmentTypeId: string,
    payload: { date: string; event: string; note?: string },
  ) =>
    request<ApiCalendarEvent>(`/equipment/types/${equipmentTypeId}/calendar-events`, tenantId, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  deleteCalendarEvent: (tenantId: string, id: string) =>
    request<{ ok: true }>(`/equipment/calendar-events/${id}`, tenantId, { method: 'DELETE' }),
}
